import { Plus, Search, Download } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Badge } from './Badge';

interface FilterBadge {
  id: string;
  label: string;
  onRemove: () => void;
}

interface HeaderListProps {
  title: string;
  buttonLabel?: string;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onButtonClick?: () => void;
  showSearch?: boolean;
  showBackButton?: boolean;
  onBackClick?: () => void;
  secondButtonLabel?: string;
  onSecondButtonClick?: () => void;
  secondButtonIcon?: React.ReactNode;
  secondButtonClassName?: string;
  thirdButtonLabel?: string;
  onThirdButtonClick?: () => void;
  thirdButtonIcon?: React.ReactNode;
  thirdButtonClassName?: string;
  filterBadges?: FilterBadge[];
}

export function HeaderList({
  title,
  buttonLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onButtonClick,
  showSearch = true,
  showBackButton,
  onBackClick,
  secondButtonLabel,
  onSecondButtonClick,
  secondButtonIcon = <Download size={20} />,
  secondButtonClassName = "flex items-center justify-center gap-2 w-full sm:w-auto bg-green-600 hover:bg-green-700",
  thirdButtonLabel,
  onThirdButtonClick,
  thirdButtonIcon,
  thirdButtonClassName = "flex items-center justify-center gap-2 w-full sm:w-auto bg-purple-600 hover:bg-purple-700",
  filterBadges = [],
}: HeaderListProps) {
  const handleClearSearch = () => {
    onSearchChange('');
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-6">
      <div className="flex items-center">
        {showBackButton && (
          <Button 
            onClick={onBackClick} 
            className="mr-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Voltar
          </Button>
        )}
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
          {filterBadges.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {filterBadges.map((badge) => (
                <Badge
                  key={badge.id}
                  variant="purple"
                  size="md"
                  removable
                  onRemove={badge.onRemove}
                >
                  {badge.label}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center w-full sm:w-auto">
        {showSearch && (
          <div className="w-full sm:w-64">
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              icon={<Search className="w-5 h-5" />}
              clearable
              onClear={handleClearSearch}
            />
          </div>
        )}
        <div className="flex gap-2">
          {buttonLabel && onButtonClick && (
            <Button
              onClick={onButtonClick}
              className="flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <Plus size={20} />
              {buttonLabel}
            </Button>
          )}
          {secondButtonLabel && onSecondButtonClick && (
            <Button
              onClick={onSecondButtonClick}
              className={secondButtonClassName}
            >
              {secondButtonIcon}
              {secondButtonLabel}
            </Button>
          )}
          {thirdButtonLabel && onThirdButtonClick && (
            <Button
              onClick={onThirdButtonClick}
              className={thirdButtonClassName}
            >
              {thirdButtonIcon}
              {thirdButtonLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
} 